import { useState } from "react";
import { Star, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/** Modal for clients to submit a review.
 *  Optional `project` prop — when present, the review is linked to that project
 *  and uses its service_type. When absent, it's a "general" testimonial. */
export function ReviewSubmitModal({ open, onClose, project = null, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [body, setBody] = useState("");
  const [displayCompany, setDisplayCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const close = () => {
    setRating(0); setHover(0); setBody(""); setDisplayCompany("");
    onClose?.();
  };

  const submit = async () => {
    if (rating === 0) { toast.error("Pick a star rating"); return; }
    if (body.trim().length < 10) { toast.error("Tell us a bit more (10+ chars)"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", {
        project_id: project?.id || null,
        rating,
        body: body.trim(),
        service_type: project?.service_type || null,
        display_company: displayCompany.trim() || null,
      });
      toast.success("Thanks — your review is in. We'll publish it shortly.");
      onSubmitted?.(data);
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto" data-testid="review-modal">
      <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-md p-6 my-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Leave a review</div>
            <h3 className="font-display text-2xl mt-1">{project ? `How was ${project.title}?` : "How was Vision Grafix?"}</h3>
            {project?.service_type && (
              <div className="text-xs text-[#a3a3a3] mt-1 capitalize">{project.service_type.replace(/_/g, " ")}</div>
            )}
          </div>
          <button onClick={close} className="text-[#a3a3a3] hover:text-white" aria-label="Close"><X size={18} /></button>
        </div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Your rating</label>
        <div className="flex gap-1 mb-5" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)}
              data-testid={`star-${n}`}
              className="text-[#FF2D2D] hover:scale-110 transition-transform"
              aria-label={`Rate ${n} stars`}
            >
              <Star
                size={32}
                weight={(hover || rating) >= n ? "fill" : "regular"}
              />
            </button>
          ))}
        </div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Tell us how it went</label>
        <textarea
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          data-testid="review-body"
          placeholder="What did you love? What could be better? Concrete examples help."
          className="w-full bg-[#050505] border border-[#262626] px-4 py-3 text-sm focus:border-[#FF2D2D] outline-none"
        />
        <div className="text-[10px] text-[#666] text-right mt-1">{body.length}/2000</div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mt-3 mb-2">Display name <span className="text-[#666] normal-case tracking-normal">(optional — leave blank to use your account name)</span></label>
        <input
          type="text"
          value={displayCompany}
          onChange={(e) => setDisplayCompany(e.target.value)}
          maxLength={120}
          data-testid="review-display"
          placeholder="e.g. Acme Co. or your full name"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none"
        />

        <div className="text-xs text-[#a3a3a3] mt-4">
          Reviews are moderated before going public. We'll never publish your email address.
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="review-submit"
            className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {submitting ? "Sending…" : "Submit review"}
          </button>
          <button onClick={close} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
        </div>
      </div>
    </div>
  );
}
